// pipManager.js
import I18n from 'i18n-js';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { create } from 'jss';
import { ConnectionStatistics } from '@eyeson/js';
import {
  ThemeProvider,
  jssPreset,
  StylesProvider,
  makeStyles,
} from '@material-ui/styles';
import { SnackbarProvider, useSnackbar } from 'notistack';
import CssBaseline from '@material-ui/core/CssBaseline';
import createMuiTheme from '../styles/muiTheme.js';
import createSnackbarConfig from '../utils/SnackbarConfig.js';
import { IconButton, Paper } from '@material-ui/core';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  PowerSettingsNew,
} from '@material-ui/icons';
import MainView from './MainView.js';
import ConnectionInfoIcon from './expansion_panels/connection/ConnectionInfoIcon.js';
import LocalStream from './streams/LocalStream.js';
import GuiIcon from './generic/Icon.js';
import RemoteMuteDialog from './dialog/RemoteMuteDialog.js';
import PromptDialog from './dialog/PromptDialog.js';
import DialogWindow from './dialog/DialogWindow.js';
import secondCamHelper from '../utils/eyesonSecondCamHelper.js';
import BadgeIcon from './generic/BadgeIcon.js';
import CountdownDialog from './dialog/CountdownDialog.js';
import OptionalTooltip from './generic/OptionalTooltip.js';

// https://mashable.com/article/google-meet-new-picture-in-picture-feature
// https://www.askdavetaylor.com/everything-to-know-about-picture-in-picture-in-google-meet/

const hasDocumentPiPSupport = 'documentPictureInPicture' in window;
const { Status } = ConnectionStatistics;
const statusNames = {};
statusNames[Status.NO_CONNECTION] = 'status:connection:no_connection';
statusNames[Status.BAD] = 'status:connection:poor';
statusNames[Status.OK] = 'status:connection:unstable';
statusNames[Status.GOOD] = 'status:connection:excellent';

const useStyles = makeStyles((theme) => ({
  tooltip: {
    margin: '4px',
  },
  activeIcon: {
    fill: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
  },
}));

const PipTooltip = (props) => {
  const { classes, PopperProps } = props;
  const pip = window.documentPictureInPicture?.window;
  const container = pip?.document?.body;

  return (
    <OptionalTooltip
      {...props}
      PopperProps={{
        ...PopperProps,
        container,
      }}
      classes={{ tooltip: classes }}
    />
  );
};

const PiPContent = ({
  closeAndReturn,
  focusOpener,
  sharedState: initialSharedState,
  pipWindow,
  toggleTheme,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [sharedState, setSharedState] = useState(initialSharedState);
  const { handleEvent, isVisible, messages } = sharedState;
  const [secondCamActive, setSecondCamActive] = useState(
    secondCamHelper.getCurrentState() === 'active'
  );
  const pendingToastsRef = useRef([]);
  const classes = useStyles();
  const countUnreadMessages = useCallback(() => {
    let count = 0;
    for (let index = 0, length = messages.length; index < length; index++) {
      if (!messages[index].isShown) {
        count++;
      }
    }
    return count;
  }, [messages]);
  const unreadMessages = countUnreadMessages();

  useEffect(() => {
    if (!pipWindow) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const stillPending = [];

      for (const { key, expiresAt } of pendingToastsRef.current) {
        if (now >= expiresAt) {
          closeSnackbar(key);
        } else {
          stillPending.push({ key, expiresAt });
        }
      }

      pendingToastsRef.current = stillPending;
    }, 500); // check twice per second

    return () => {
      clearInterval(interval);
      pendingToastsRef.current = [];
    };
  }, [pipWindow, closeSnackbar]);

  useEffect(() => {
    pipWindow.updateState = (newState) => {
      setSharedState((prev) => ({
        ...prev,
        ...newState,
      }));
    };
    pipWindow.enqueueSnackbar = (content, options = {}) => {
      const key = enqueueSnackbar(content, options);
      const { autoHideDuration = 2000 } = options;
      const expiresAt = Date.now() + autoHideDuration;
      pendingToastsRef.current.push({ key, expiresAt });
      return key;
    };
    pipWindow.onThemeToggle = (details) => {
      toggleTheme(details);
    };
    return () => {
      pipWindow.updateState = null;
      pipWindow.enqueueSnackbar = null;
      pipWindow.onThemeToggle = null;
      pendingToastsRef.current = [];
    };
  }, [pipWindow, enqueueSnackbar, toggleTheme]);

  useEffect(() => {
    setSecondCamActive(secondCamHelper.getCurrentState() === 'active');
    const callback = () => {
      setSecondCamActive(secondCamHelper.getCurrentState() === 'active');
    };
    secondCamHelper.onEvent(callback);
    return () => {
      secondCamHelper.offEvent(callback);
    };
  }, []);

  const toggleAudio = () => {
    if (sharedState.recvOnlyMode) {
      return;
    }
    sharedState.handleEvent({ type: 'toggle_audio' });
  };

  const toggleVideo = () => {
    if (sharedState.recvOnlyMode) {
      return;
    }
    sharedState.handleEvent({ type: 'toggle_video' });
  };

  const closeDialog = () => {
    sharedState.handleEvent({ type: 'hide_dialog' });
  };

  const showPresentation = () => {
    focusOpener();
    sharedState.handleEvent({ type: 'presentation' });
  };

  const showChat = () => {
    focusOpener();
    sharedState.setOpenPanel('chat');
  };

  const handleExit = () => {
    const type = sharedState.isGuest ? 'exit_prompt_guest' : 'exit_prompt';
    sharedState.handleEvent({ type });
    closeAndReturn();
  };

  const tooltip = (key, state) => {
    if (sharedState.recvOnlyMode && (key === 'audio' || key === 'video')) {
      return null;
    }
    if (state === true || state === false) {
      key += state ? ':on' : ':off';
    }
    return I18n.t('tooltip:' + key);
  };

  return (
    <div className="eyeson-room-pip">
      <main role="main" id="main">
        <MainView
          eco={false}
          onEvent={handleEvent}
          isPhone={false}
          isVisible={isVisible}
          usePersistentAudio={true}
          insidePip={true}
          closeAndReturn={closeAndReturn}
          {...sharedState}
        ></MainView>
        {sharedState.active.sfu &&
          !sharedState.solo &&
          sharedState.active.video && (
            <div className="self-view">
              <LocalStream
                user={sharedState.user}
                stream={sharedState.localStream}
                active={sharedState.active}
                visible={true}
                onEvent={handleEvent}
                playback={sharedState.playback}
                className="self-view-video"
                participants={sharedState.participants}
                voiceActivity={sharedState.voiceActivity}
                virtualBackground={sharedState.virtualBackground}
                insidePip={true}
              />
            </div>
          )}
      </main>
      <Paper square component="section" className="eyeson-container toolbar">
        <div className="status-elements">
          {sharedState.connectionStatistics && (
            <PipTooltip
              title={I18n.t(
                statusNames[sharedState.connectionStatistics.status]
              )}
              classes={classes.tooltip}
            >
              <div className="connection-info">
                <ConnectionInfoIcon data={sharedState.connectionStatistics} />
              </div>
            </PipTooltip>
          )}
          {sharedState.active.live_stream && (
            <PipTooltip
              title={I18n.t('label:streaming')}
              classes={classes.tooltip}
            >
              <div className="active-marker">
                <GuiIcon
                  type="settings_input_antenna"
                  className={classes.activeIcon}
                />
              </div>
            </PipTooltip>
          )}
          {sharedState.active.recording && !sharedState.hideRecording && (
            <PipTooltip
              title={I18n.t('label:recording')}
              classes={classes.tooltip}
            >
              <div className="active-marker">
                <GuiIcon type="recording" className={classes.activeIcon} />
              </div>
            </PipTooltip>
          )}
        </div>
        <div className="toggle-elements">
          <PipTooltip
            title={tooltip('video', sharedState.active.video)}
            classes={classes.tooltip}
          >
            <IconButton
              disabled={sharedState.recvOnlyMode}
              onClick={toggleVideo}
              aria-label={I18n.t('label:toggle_video', {
                defaultValue: 'Toggle video',
              })}
            >
              {sharedState.active.video ? <Videocam /> : <VideocamOff />}
            </IconButton>
          </PipTooltip>
          <PipTooltip
            title={tooltip('audio', sharedState.active.audio)}
            classes={classes.tooltip}
          >
            <IconButton
              disabled={sharedState.recvOnlyMode}
              onClick={toggleAudio}
              aria-label={I18n.t('label:toggle_audio', {
                defaultValue: 'Toggle audio',
              })}
            >
              {sharedState.active.audio ? <Mic /> : <MicOff />}
            </IconButton>
          </PipTooltip>
          {unreadMessages > 0 && (
            <PipTooltip
              title={I18n.t('label:open_chat')}
              classes={classes.tooltip}
            >
              <IconButton onClick={showChat}>
                <BadgeIcon icon="message" count={unreadMessages} small={true} />
              </IconButton>
            </PipTooltip>
          )}
          {(sharedState.active.hasPresenter ||
            sharedState.active.screenAsVideo ||
            sharedState.active.screenAddition ||
            sharedState.active.tabAudio ||
            sharedState.active.youtubePopup ||
            secondCamActive) && (
            <PipTooltip
              title={I18n.t('label:presentation')}
              classes={classes.tooltip}
            >
              <IconButton
                onClick={showPresentation}
                aria-label={I18n.t('label:presentation', {
                  defaultValue: 'Presentation',
                })}
              >
                <GuiIcon type="presentation" className={classes.activeIcon} />
              </IconButton>
            </PipTooltip>
          )}
        </div>
        <div className="exit-elements">
          <PipTooltip title={tooltip('exit')} classes={classes.tooltip}>
            <IconButton
              onClick={handleExit}
              aria-label={I18n.t('label:exit', { defaultValue: 'Exit' })}
            >
              <PowerSettingsNew />
            </IconButton>
          </PipTooltip>
        </div>
      </Paper>
      {sharedState.visible.dialog === 'remote_mute' && (
        <DialogWindow
          id="pip-remote-mute"
          title={I18n.t('dialog:remote_mute:header')}
          onClose={closeDialog}
          role="alertdialog"
          useAriaDescription
          container={pipWindow.document.body}
        >
          <RemoteMuteDialog
            dialogId="pip-remote-mute"
            user={sharedState.remoteMute.user}
            onClick={closeDialog}
          />
        </DialogWindow>
      )}
      {sharedState.visible.dialog === 'recording_started' && (
        <DialogWindow
          id="pip-recording"
          role="alertdialog"
          title={I18n.t('dialog:title:recording_started')}
          onClose={closeDialog}
          useAriaDescription
          container={pipWindow.document.body}
        >
          <PromptDialog
            dialogId="pip-recording"
            message={`${sharedState.recording.user} ${I18n.t(
              'dialog:recording:started'
            )}`}
            actionLabel={I18n.t('dialog:prompt:agree')}
            cancelLabel={I18n.t('dialog:prompt:mute')}
            onAction={() => {
              sharedState.handleEvent({ type: 'accept_recording' });
              closeDialog();
            }}
            onClose={() => sharedState.handleEvent({ type: 'mute' })}
          />
        </DialogWindow>
      )}
      {sharedState.visible.dialog === 'inactivity' && (
        <DialogWindow
          id="pip-inactive-dialog"
          title={I18n.t('dialog:title:inactive')}
          onClose={closeDialog}
          role="alertdialog"
          useAriaDescription
          container={pipWindow.document.body}
        >
          <CountdownDialog
            dialogId="pip-inactive-dialog"
            title={I18n.t('dialog:title:inactive')}
            message={I18n.t('message:inactive')}
            startTime={sharedState.config.times.dialog}
            actionLabel={I18n.t('dialog:prompt:extend')}
            onCountdownEnd={() => {}}
            onAction={() => {
              sharedState.handleEvent({
                type: 'active',
                activityMonitor: sharedState.activityMonitor,
              });
            }}
          />
        </DialogWindow>
      )}
    </div>
  );
};

const App = ({
  jssInstance,
  sharedState,
  closeAndReturn,
  focusOpener,
  pipWindow,
}) => {
  const [theme, setTheme] = useState(sharedState.theme);

  const toggleTheme = (details = {}) => {
    let newTheme = theme === 'dark' ? 'light' : 'dark';
    if (details.color) {
      newTheme = details.color;
    }
    setTheme(newTheme);
    pipWindow.document.body.dataset.theme = newTheme;
  };

  return (
    <StylesProvider jss={jssInstance}>
      <ThemeProvider theme={createMuiTheme(theme)}>
        <SnackbarProvider {...createSnackbarConfig()}>
          <CssBaseline />
          <PiPContent
            closeAndReturn={closeAndReturn}
            focusOpener={focusOpener}
            sharedState={sharedState}
            pipWindow={pipWindow}
            toggleTheme={toggleTheme}
          />
        </SnackbarProvider>
      </ThemeProvider>
    </StylesProvider>
  );
};

async function openDocumentPiP(opener, sharedState, onClose) {
  if (!hasDocumentPiPSupport) return null;

  const options = {
    width: 400,
    height: sharedState.widescreen ? 277 : 352,
  };
  const pipWindow = await window.documentPictureInPicture.requestWindow(
    options
  );
  const { document } = pipWindow;

  const root = document.createElement('div');
  root.id = 'root';
  document.body.appendChild(root);
  document.body.dataset.container = 'pip';
  if (sharedState.widescreen) {
    document.body.dataset.widescreen = true;
  }
  document.body.dataset.theme = sharedState.theme;
  opener.document
    .querySelectorAll(
      'style:not([data-jss]),link[rel="stylesheet"][href*="static/css"]'
    )
    .forEach((style) => {
      document.head.appendChild(style.cloneNode(true));
    });

  const jssInstance = create({
    ...jssPreset(),
    insertionPoint: document.head,
  });

  pipWindow.addEventListener(
    'pagehide',
    () => {
      if (root) {
        ReactDOM.unmountComponentAtNode(root);
      }
      onClose();
    },
    { once: true }
  );

  sharedState.docPip = pipWindow;

  const focusOpener = () => {
    opener.focus();
  };

  const closeAndReturn = () => {
    pipWindow.close();
    opener.focus();
  };

  ReactDOM.render(
    <App
      jssInstance={jssInstance}
      closeAndReturn={closeAndReturn}
      focusOpener={focusOpener}
      sharedState={sharedState}
      pipWindow={pipWindow}
    />,
    root
  );

  return pipWindow;
}

export { hasDocumentPiPSupport, openDocumentPiP };
